import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { Col, Form, Row, Spin, Typography } from 'antd'
import { get, isEmpty, map, some } from 'lodash'
import PropTypes from 'prop-types'

import { areSameValue, mergeArrayOfObjects } from '../../../core/utils'

import {
  DirtyPromptForm,
  FormButtons,
  FormLazyTreeSelect,
} from '../../../common/components/sections'
import { StyledVerticalSpace } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { showErrorNotification, transformTree } from '../../../common/helpers'
import { useFormDirty, useUserHasPermissions } from '../../../common/hooks'
import {
  idPropType,
  optionPropType,
  treeOptionPropType,
} from '../../../common/prop-types'

import { useUpdateIngredientAlternativeIngredientsDetailsMutation } from '../../hooks'
import { alternativeIngredientPropType } from '../../prop-types'
import { setIngredientPageIsDirty } from '../../redux/actions'
import { IngredientConflictingErrors } from '../sections'

import { ingredientAlternativeIngredientsDetailsFormInitialValues } from './initial-values'

const IngredientAlternativeIngredientsDetailsForm = ({
  ingredientId,
  alternativeIngredients,
  ingredientOptions,
  dietOptions,
  groupedFlatTreeDataByValue,
  flatTreeData,
}) => {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)

  const isFormEnabled = useUserHasPermissions([
    PERMISSIONS.INGREDIENT.SAVE_ALTERNATIVE_INGREDIENTS,
  ])

  const [
    updateIngredientAlternativeIngredientsDetails,
    { isLoading: isSaveLoading, error: saveError },
  ] = useUpdateIngredientAlternativeIngredientsDetailsMutation()

  const initialValues = useMemo(
    () =>
      ingredientAlternativeIngredientsDetailsFormInitialValues(
        alternativeIngredients,
      ),
    [alternativeIngredients],
  )

  const ingredientIdsRules = useMemo(
    () => [
      () => ({
        validator(_, value) {
          try {
            if (areSameValue(value, ingredientId)) {
              throw new Error(`Can't select owning ingredient`)
            }

            return Promise.resolve()
          } catch (error) {
            return Promise.reject(error)
          }
        },
      }),
    ],
    [ingredientId],
  )

  const ingredientTreeDataGroupedByDietId = useMemo(
    () =>
      mergeArrayOfObjects(
        map(dietOptions, ({ value, excludedIngredients }) => ({
          [value]: transformTree(ingredientOptions, node =>
            some(excludedIngredients, ({ id }) => node.value === id)
              ? { ...node, disabled: true }
              : node,
          ),
        })),
      ),
    [dietOptions, ingredientOptions],
  )

  const handleSaveData = useCallback(
    data => updateIngredientAlternativeIngredientsDetails(ingredientId, data),
    [ingredientId, updateIngredientAlternativeIngredientsDetails],
  )

  useEffect(() => {
    if (saveError) {
      showErrorNotification({
        title: 'Ingredient details error',
        description: saveError.message,
      })
    }
  }, [saveError])

  useEffect(() => {
    resetForm()
  }, [alternativeIngredients, resetForm])

  useEffect(() => {
    dispatch(
      setIngredientPageIsDirty({ isAlternativeIngredientsDirty: isDirty }),
    )
  }, [dispatch, isDirty])

  const { conflicts } = saveError || {}

  return isSaveLoading ? (
    <Spin />
  ) : (
    <DirtyPromptForm
      isDirty={isDirty}
      form={form}
      initialValues={initialValues}
      name="alternativeIngredientsForm"
      onFinish={handleSaveData}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <Typography.Title level={3}>Alternative Ingredients</Typography.Title>
      {!isEmpty(conflicts) && (
        <IngredientConflictingErrors errors={conflicts} />
      )}
      <StyledVerticalSpace>
        {map(dietOptions, ({ value, label }) => (
          <Row key={value}>
            <Col span={4}>
              <Typography.Text>{`${label} Alternatives:`}</Typography.Text>
            </Col>
            <Col span={20}>
              <FormLazyTreeSelect
                rules={ingredientIdsRules}
                disabled={!isFormEnabled}
                form={form}
                name={['alternativeIngredients', `${value}`]}
                showSearch
                allowClear
                treeData={get(ingredientTreeDataGroupedByDietId, value)}
                placeholder={`Select a new ${label} ingredient`}
                groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
                flatTreeData={flatTreeData}
                initialValue={get(initialValues.alternativeIngredients, value)}
                noStyle
              />
            </Col>
          </Row>
        ))}
        <FormButtons
          permissions={[PERMISSIONS.INGREDIENT.SAVE_ALTERNATIVE_INGREDIENTS]}
          isDisabled={!isDirty}
          onReset={resetForm}
        />
      </StyledVerticalSpace>
    </DirtyPromptForm>
  )
}

IngredientAlternativeIngredientsDetailsForm.propTypes = {
  ingredientId: idPropType.isRequired,
  alternativeIngredients: PropTypes.arrayOf(alternativeIngredientPropType),
  ingredientOptions: PropTypes.arrayOf(treeOptionPropType.isRequired)
    .isRequired,
  dietOptions: PropTypes.arrayOf(optionPropType.isRequired).isRequired,
  groupedFlatTreeDataByValue: PropTypes.object.isRequired,
  flatTreeData: PropTypes.arrayOf(PropTypes.object).isRequired,
}

IngredientAlternativeIngredientsDetailsForm.defaultProps = {
  alternativeIngredients: [],
}

export default IngredientAlternativeIngredientsDetailsForm
